<template>
    <div class="food-v3-container">
        <!-- Stats Header (Keep existing stats logic as it was good, but strictly outside the main white card) -->
        <FoodStatsNavigation :stats="stats" activeTab="carte" @click="navigateToIndex" />

        <!-- Main White Container -->
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ mode === 'edit' ? $t('food.packForm.titleEdit') : $t('food.packForm.titleCreate') }}
                </h1>
            </div>
            <form @submit.prevent="handleSubmit">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- LEFT COLUMN -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- General Info Card -->
                        <div
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <!-- Name -->
                            <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                <CustomInput
                                    v-model="form.name"
                                    :label="$t('food.packForm.name')"
                                    :placeholder="$t('food.packForm.namePlaceholder')"
                                    :required="true"
                                    :state="getState('name')"
                                    :errorText="errors.name"
                                    @input="clearError('name')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>

                            <!-- Image -->
                            <div class="ppv2-w-full ppv2-px-3">
                                <div class="ppv2-mt-4 ppv2-text-center">
                                    <CustomInputUpload
                                        v-model="form.image"
                                        :label="$t('food.packForm.imageLabel')"
                                        @input="onImageUpload"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Pricing Card -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.packForm.pricingTitle') }}
                                </h2>
                                <!-- <BaseSwitch
                                    v-model="form.has_discount"
                                    label="Appliquer une remise"
                                    size="md"
                                /> -->
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6 ppv2-mx-3">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        v-model="form.price"
                                        :label="$t('food.sellingPrice')"
                                        placeholder="0.00"
                                        type="number"
                                        step="any"
                                        :rightIcon="$symbolCurrency"
                                        :required="true"
                                        :state="getState('price')"
                                        :errorText="errors.price"
                                        @input="clearError('price')"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <BaseSelect
                                        v-model="form.vat_rate"
                                        :label="$t('food.vat_rate')"
                                        :options="vatRates"
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="- Sélectionner -"
                                        :required="true"
                                        :error="errors.vat_rate"
                                        @input="clearError('vat_rate')"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    />
                                </div>

                                <div v-if="form.has_discount" class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        v-model="form.discounted_price"
                                        :label="$t('food.packForm.newPriceLabel')"
                                        placeholder="0.00"
                                        type="number"
                                        step="any"
                                        :rightIcon="$symbolCurrency"
                                        @input="
                                            calculateDiscountPercentage()
                                            clearError('discounted_price')
                                        "
                                        :state="getState('discounted_price')"
                                        :errorText="errors.discounted_price"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    />
                                </div>
                                <div v-if="form.has_discount" class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        v-model="form.discount_percentage"
                                        :label="$t('food.packForm.discountPercentLabel')"
                                        placeholder="0"
                                        type="number"
                                        step="1"
                                        rightIcon="%"
                                        @input="
                                            calculateDiscountedPrice()
                                            clearError('discount_percentage')
                                        "
                                        :state="getState('discount_percentage')"
                                        :errorText="errors.discount_percentage"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- Composition Card -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.packForm.compositionTitle') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>

                            <div class="ppv2-p-6 ppv2-mx-3">
                                <div v-for="(composition, index) in form.compositions" :key="'comp-' + index">
                                    <!-- Row 1: Name + Buttons -->
                                    <div class="d-flex flex-row align-items-end ppv2-mb-[20px] ppv2-gap-3">
                                        <div class="ppv2-flex" :style="{ width: `calc(100% - 120px)` }">
                                            <CustomInput
                                                v-model="composition.name"
                                                :label="$t('food.packForm.compositionNameLabel')"
                                                :placeholder="$t('food.packForm.compositionNamePlaceholder')"
                                                classContainerAdd="!ppv2-mb-0"
                                                :state="
                                                    errors[`compositions.${index}.name`]
                                                        ? 'error'
                                                        : composition.name
                                                          ? 'valid'
                                                          : 'default'
                                                "
                                                :errorText="errors[`compositions.${index}.name`]"
                                                @input="clearError(`compositions.${index}.name`)"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            />
                                        </div>
                                        <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3" style="width: 120px">
                                            <div
                                                class="ppv2-bg-white ppv2-bg-opacity-5 border_duration_experience ppv2-opacity-75 ppv2-text-black d-flex align-items-center justify-center"
                                                style="width: 42px"
                                            >
                                                <v-btn
                                                    @click="addComposition"
                                                    class="transparent-btn ppv2-text-black"
                                                    depressed
                                                    small
                                                    outlined
                                                >
                                                    <Plus :size="24" />
                                                </v-btn>
                                            </div>
                                            <div
                                                class="ppv2-bg-white ppv2-bg-opacity-5 border_duration_experience ppv2-opacity-75 ppv2-text-black d-flex align-items-center justify-center"
                                                style="width: 42px"
                                            >
                                                <v-btn
                                                    class="transparent-btn ppv2-text-black"
                                                    depressed
                                                    small
                                                    @click="removeComposition(index)"
                                                    :disabled="form.compositions.length <= 1"
                                                    :class="{
                                                        'ppv2-opacity-50 ppv2-cursor-not-allowed': form.compositions.length <= 1,
                                                    }"
                                                >
                                                    <Minus :size="24" />
                                                </v-btn>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Row 2: Select -->
                                    <div class="d-flex flex-row align-items-center mb-2 ppv2-pr-3">
                                        <div :style="{ width: `calc(100% - 120px)` }">
                                            <BaseSelect
                                                v-model="composition.product_ids"
                                                :placeholder="$t('food.packForm.compositionSelectPlaceholder')"
                                                :options="products"
                                                optionLabel="name"
                                                optionValue="id"
                                                :multiple="true"
                                                :error="errors[`compositions.${index}.product_ids`]"
                                                @input="clearError(`compositions.${index}.product_ids`)"
                                                classAdded="borderInput !ppv2-rounded-xl"
                                                customLabelClass="poppinsLight ppv2-text-black"
                                                :customSizeLabel="15"
                                            />
                                        </div>
                                    </div>
                                    <v-divider class="d-flex" v-if="index < form.compositions.length - 1"></v-divider>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- RIGHT COLUMN -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <!-- Actions / Availability Card -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-space-y-4">
                                <!-- Activé Toggle -->
                                <BaseSwitch
                                    v-model="form.active"
                                    :label="$t('food.packForm.activeLabel')"
                                    :boxed="true"
                                    :variant="form.active ? 'success' : 'gray'"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />

                                <!-- Afficher sur la carte -->
                                <BaseSwitch
                                    v-model="form.show_on_menu"
                                    :label="$t('food.packForm.showOnMenuLabel')"
                                    :boxed="true"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />

                                <a
                                    :href="frontUrl ? frontUrl + '/discover/food' : '#'"
                                    target="_blank"
                                    class="ppv2-flex ppv2-items-center ppv2-space-x-2 ppv2-w-full ppv2-border ppv2-px-3 ppv2-py-2 ppv2-border-[#E5E7EB] justify-content-center gap-2"
                                    :style="{ fontSize: normalizeFontSize(14), boxShadow: '0px 2px 4px 0px #00000040' }"
                                >
                                    <span class="ppv2-text-black"
                                        >{{ $t('food.packForm.viewOnMap') }}
                                        <ExternalLink :size="16" />
                                    </span>
                                </a>
                            </div>
                        </v-card>

                        <!-- Takeaway Card -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.packForm.takeawayTitle') }}
                                </h2>

                                <BaseSwitch
                                    v-model="form.is_takeaway_enabled"
                                    :label="form.is_takeaway_enabled ? $t('common.yes') : $t('common.no')"
                                    :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                            </div>
                            <v-divider class="ppv2-m-0" v-if="form.is_takeaway_enabled"></v-divider>
                            <div v-if="form.is_takeaway_enabled" class="ppv2-p-6">
                                <CustomInput
                                    v-model="form.price_takeaway"
                                    :label="$t('food.sellingPrice')"
                                    placeholder="0.00"
                                    type="number"
                                    step="any"
                                    :rightIcon="$symbolCurrency"
                                    classContainerAdd="ppv2-mb-4"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    :state="getState('price_takeaway')"
                                    :errorText="errors.price_takeaway"
                                    @input="clearError('price_takeaway')"
                                />
                                <BaseSelect
                                    v-model="form.vat_rate_takeaway"
                                    :label="$t('food.vat_rate')"
                                    :options="vatRates"
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="- Sélectionner -"
                                    :error="errors.vat_rate_takeaway"
                                    @input="clearError('vat_rate_takeaway')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>
                        </v-card>

                        <!-- Delivery Card -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-p-6 ppv2-flex ppv2-items-center ppv2-justify-between">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('food.packForm.availableDelivery') }}
                                </h2>
                                <BaseSwitch
                                    v-model="form.is_delivery_enabled"
                                    :label="form.is_delivery_enabled ? $t('common.yes') : $t('common.no')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                    :class="{
                                        '!ppv2-text-[#15803d]': form.is_delivery_enabled,
                                        '!ppv2-text-gray-500': !form.is_delivery_enabled,
                                    }"
                                />
                            </div>
                            <v-divider class="ppv2-m-0" v-if="form.is_delivery_enabled"></v-divider>
                            <div v-if="form.is_delivery_enabled" class="ppv2-p-6">
                                <CustomInput
                                    v-model="form.price_delivery"
                                    :label="$t('food.sellingPrice')"
                                    placeholder="0.00"
                                    type="number"
                                    step="any"
                                    :rightIcon="$symbolCurrency"
                                    classContainerAdd="ppv2-mb-4"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    :state="getState('price_delivery')"
                                    :errorText="errors.price_delivery"
                                    @input="clearError('price_delivery')"
                                />
                                <BaseSelect
                                    v-model="form.vat_rate_delivery"
                                    :label="$t('food.vat_rate')"
                                    :options="vatRates"
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="- Sélectionner -"
                                    :error="errors.vat_rate_delivery"
                                    @input="clearError('vat_rate_delivery')"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                />
                            </div>
                        </v-card>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <!-- Footer: Retour & Valider -->
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="navigateToIndex('packs')" />
                            <BaseButton
                                variant="primary"
                                :loading="isSubmitting"
                                @click="handleSubmit"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ isSubmitting ? $t('common.saving') : $t('common.confirm') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </form>
        </v-app>
    </div>
</template>

<script>
import { Plus, Minus, ExternalLink, ArrowLeft, Loader2 } from 'lucide-vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BackButton from '../../Shared/BackButton.vue'
import normalizeSize from '../../../utils/normalizeSize'
import FoodStatsNavigation from './FoodStatsNavigation.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'BoFoodV3PackForm',
    components: {
        Plus,
        Minus,
        ExternalLink,
        ArrowLeft,
        Loader2,
        CustomInput,
        CustomInputUpload,
        BaseSelect,
        BaseSwitch,
        BaseButton,
        BackButton,
        FoodStatsNavigation,
    },
    props: {
        mode: {
            type: String,
            default: 'create',
            validator: (val) => ['create', 'edit'].includes(val),
        },
        type: {
            type: String,
            default: 'pack',
        },
        packId: {
            type: [String, Number],
            default: null,
        },
        packUuid: {
            type: String,
            default: null,
        },
        establishment: {
            type: Object,
            default: () => ({}),
        },
        frontUrl: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: true,
            isSubmitting: false,
            form: {
                name: '',
                image: null,
                price: '',
                vat_rate: null,
                has_discount: false,
                discounted_price: null,
                discount_percentage: null,
                compositions: [{ name: '', category_ids: [], product_ids: [] }],
                price_takeaway: '',
                vat_rate_takeaway: null,
                price_delivery: '',
                vat_rate_delivery: null,
                active: true,
                show_on_menu: true,
                is_takeaway_enabled: false,
                is_delivery_enabled: false,
            },
            errors: {},
            stats: {
                products: 0,
                categories: 0,
                packs: 0,
                options: 0,
                tables: 0,
            },
            categories: [],
            products: [],
        }
    },
    computed: {
        vatRates() {
            const tvas = this.establishment?.pays?.tvas || []
            if (tvas.length > 0) {
                return tvas.map((tva) => {
                    const val = parseFloat(tva.pourcentage)
                    return {
                        value: val,
                        label: `${val}%`,
                    }
                })
            }
            return [
                { value: 20, label: '20%' },
                { value: 10, label: '10%' },
                { value: 5.5, label: '5.5%' },
            ]
        },
    },
    watch: {
        'form.active'(newValue) {
            // If pack is deactivated, also hide from menu
            if (!newValue) {
                this.form.show_on_menu = false
            }
        },
    },
    mounted() {
        this.fetchInitialData()
        if (this.mode === 'edit' && this.packUuid) {
            this.fetchPackData()
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },
        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.form[field]) return 'valid'
            return 'default'
        },
        async fetchInitialData() {
            try {
                // Fetch stats
                const statsResponse = await axios.get('/api/v3/food/stats')
                this.stats = statsResponse.data.data || statsResponse.data

                // Fetch categories
                const categoriesResponse = await axios.get('/back-office/v3/food/categories/fast-list')
                this.categories = categoriesResponse.data

                // Fetch products (needed for composition logic if we re-introduce product selection, though simplified now)
                const productsResponse = await axios.get('/back-office/v3/food/list?itemsPerPage=1000')
                this.products = productsResponse.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },

        async fetchPackData() {
            try {
                const response = await axios.get(`/back-office/v3/food/packs/show/${this.packUuid}`)
                const pack = response.data.data

                this.form = {
                    ...this.form,
                    name: pack.name,
                    price: pack.price != null ? parseFloat(pack.price).toFixed(2) : '',
                    vat_rate: pack.vat_rate != null ? parseFloat(pack.vat_rate) : null,
                    // If backend supports discount, map it here. Assuming standard fields for now.
                    // If backend supports discount, map it here. Assuming standard fields for now.
                    active: !!pack.active,
                    show_on_menu: pack.show_on_menu !== undefined ? !!pack.show_on_menu : true,
                    compositions: pack.compositions || [{ name: '', category_ids: [], product_ids: [] }],
                    image: pack.image,
                    price_takeaway: pack.price_takeaway != null ? parseFloat(pack.price_takeaway).toFixed(2) : '',
                    vat_rate_takeaway: pack.vat_rate_takeaway != null ? parseFloat(pack.vat_rate_takeaway) : null,
                    price_delivery: pack.price_delivery != null ? parseFloat(pack.price_delivery).toFixed(2) : '',
                    vat_rate_delivery: pack.vat_rate_delivery != null ? parseFloat(pack.vat_rate_delivery) : null,
                    is_takeaway_enabled: !!pack.price_takeaway,
                    is_delivery_enabled: !!pack.price_delivery,
                }

                // Handle image if needed (usually just show preview, not set file object)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching pack data:', error)
            }
        },

        addComposition() {
            this.form.compositions.push({
                name: '',
                category_ids: [],
                product_ids: [],
            })
        },

        removeComposition(index) {
            if (this.form.compositions.length > 1) {
                this.form.compositions.splice(index, 1)
            }
        },

        getProductsForCategories(categoryIds) {
            if (!categoryIds || categoryIds.length === 0) return []
            return this.products.filter((p) => categoryIds.includes(p.category_id))
        },

        calculateDiscountPercentage() {
            if (this.form.price && this.form.discounted_price) {
                const original = parseFloat(this.form.price)
                const discounted = parseFloat(this.form.discounted_price)
                if (original > 0) {
                    this.form.discount_percentage = Math.round(((original - discounted) / original) * 100)
                }
            }
        },

        calculateDiscountedPrice() {
            if (this.form.price && this.form.discount_percentage) {
                const original = parseFloat(this.form.price)
                const percentage = parseFloat(this.form.discount_percentage)
                if (original > 0) {
                    this.form.discounted_price = (original * (1 - percentage / 100)).toFixed(2)
                }
            }
        },

        async handleSubmit() {
            this.isSubmitting = true
            this.errors = {}

            try {
                const formData = new FormData()
                formData.append('name', this.form.name)
                formData.append('price', this.form.price)
                formData.append('vat_rate', this.form.vat_rate)
                formData.append('active', this.form.active ? 1 : 0)
                formData.append('show_on_menu', this.form.show_on_menu ? 1 : 0)

                // Takeaway fields
                formData.append('is_takeaway_enabled', this.form.is_takeaway_enabled ? 1 : 0)
                if (this.form.is_takeaway_enabled) {
                    formData.append('price_takeaway', this.form.price_takeaway || this.form.price)
                    formData.append('vat_rate_takeaway', this.form.vat_rate_takeaway || this.form.vat_rate)
                }

                // Delivery fields
                formData.append('is_delivery_enabled', this.form.is_delivery_enabled ? 1 : 0)
                if (this.form.is_delivery_enabled) {
                    formData.append('price_delivery', this.form.price_delivery || this.form.price)
                    formData.append('vat_rate_delivery', this.form.vat_rate_delivery || this.form.vat_rate)
                }

                if (this.form.image instanceof File) {
                    formData.append('image', this.form.image)
                } else if (this.form.image === null) {
                    formData.append('remove_image', '1')
                }

                // Append compositions as JSON
                formData.append('compositions', JSON.stringify(this.form.compositions))

                const url =
                    this.mode === 'create'
                        ? '/back-office/v3/food/packs/store'
                        : `/back-office/v3/food/packs/update/${this.packUuid}`

                await axios.post(url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })

                window.location.href = '/back-office/v3/food?tab=carte'
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response?.status === 422) {
                    // Laravel renvoie un TABLEAU de messages par champ ; le template les passe à
                    // :errorText, qui attend une chaîne. On ne garde que le premier.
                    const errors = {}
                    const responseErrors = error.response.data.errors || {}
                    Object.keys(responseErrors).forEach((field) => {
                        const messages = responseErrors[field]
                        errors[field] = Array.isArray(messages) ? messages[0] : messages
                    })
                    this.errors = errors
                } else {
                    console.error('Error saving pack:', error)
                }
            } finally {
                this.isSubmitting = false
            }
        },

        goBack() {
            window.history.back()
        },

        navigateToIndex(tab) {
            window.location.href = `/back-office/v3/food?tab=${tab}`
        },
        onImageUpload(file) {
            this.form.image = file
        },
    },
}
</script>

<style scoped lang="scss">
.food-v3-container {
    padding: 0;
}

// Stats Cards Styling
.statsCard {
    background: rgba(30, 30, 46, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.1);
    min-width: 180px;
    flex: 1;

    &:hover {
        background: rgba(30, 30, 46, 0.6);
    }
}

.statsCardActive {
    background: white !important;
    border: none;
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);

    ::v-deep .ppv2-text-opacity-80 {
        color: #6b7280 !important;
    }
}

// Override switch color for green theme
::v-deep .custom-switch-green {
    .bg-purple,
    .border-purple {
        background-color: #16a34a !important; // green-600
        border-color: #16a34a !important;
    }
}

.border_duration_experience {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-radius: 12px;
    border: 1px solid #545595;
    min-height: 40px;
}

.border_duration_experience label {
    font-family: 'Poppins Regular', sans-serif;
}

.transparent-btn {
    border-radius: 12px !important;
    width: 100% !important;
    height: 100% !important;
    box-shadow: none !important;
    position: absolute;
    left: 0;
    top: 0;
    border: none;
    min-width: 40px !important;
}

.transparent-btn:hover {
    background: #d9d9d91a !important;
}
</style>

<!-- Non-scoped style to override parent container background -->
<style lang="scss">
// Override the main-container white background for Food V3 module
// Using class added via JavaScript for better browser compatibility
#main-container.food-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

// Also support the :has() selector for modern browsers
#main-container:has(.food-v3-container) {
    background: transparent !important;
}

// Dark mode support
.dark #main-container.food-v3-no-bg,
.dark #main-container:has(.food-v3-container) {
    background: transparent !important;
}
</style>
